import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';

import { DataCollection } from '../../../shared/models/site-content/data-collection.model';
import { DataCollectionMilestone } from '../../../shared/models/milestones/data-collection-milestone.model';
import { SiteMilestoneName } from '../../../shared/models/milestones/milestone-enums';
import { MilestoneService } from '../../../shared/services/milestone.service';
import { SiteMilestoneDialog, SiteMilestoneDialogData } from '../../../shared/dialogs/site-milestone/site-milestone.dialog';
import { AccessibilityHelper } from '../../../shared/accessibility.helper';
import { ConfirmationDialogComponent, ConfirmationDialogModel } from '../../../shared/dialogs/confirmation/confirmation-dialog.component';
import { ConfirmationDialogEnums } from '../../../shared/enums/confirmation-dialog.enum';
import { IConfirmationDialog } from '../../../shared/interfaces/confirmation-dialog.interface';
import { SiteMilestonesSummary } from '../../../shared/models/milestones/site-milestones-summary.model';
import { IRefreshData } from '../../../shared/interfaces/irefresh-data.interface';

@Component({
    selector: 'app-site-milestones',
    templateUrl: './site-milestones.component.html',
    styleUrls: ['./site-milestones.component.scss'],
    standalone: false
})
export class SiteMilestonesComponent implements OnInit, IRefreshData {
    // Properties.
    @Input() site: DataCollection;
    @Input() editMode: boolean = true; // Read only vs. allow edits.
    @Input() isSiteLevelAccess: boolean = true;

    // Output(s) when in view only mode.
    @Output() onEdit: EventEmitter<DataCollectionMilestone[]> = new EventEmitter();
    // Output(s) when in edit mode.
    @Output() close: EventEmitter<any> = new EventEmitter();

    // Read only values.
    //private 

    private siteMilestonesSummary: SiteMilestonesSummary = new SiteMilestonesSummary();

    // Constructor.
    public constructor(public dialog: MatDialog,
        private milestoneService: MilestoneService) {
        return;
    }

    // Life cycle methods.
    public ngOnInit(): void {
        this.refreshData();
        return;
    }

    // Implement IRefreshData
    public refreshData(): void {
        this.retrieveMilestones();
    }

    // Methods called by my HTML code.
    public get EditMode(): boolean {
        return this.editMode;
    }

    public get IsSiteLevelAccess(): boolean {
        return this.isSiteLevelAccess;
    }

    public get Milestones(): DataCollectionMilestone[] {
        //return this.milestones;
        return this.siteMilestonesSummary.milestones;
    }

    public get NewMilestoneButtonDisabled(): boolean {
        let availableMilestoneNames: string[] = this.getAvailableMilestoneNames();

        return ((availableMilestoneNames == null) || (availableMilestoneNames.length == 0));
    }

    // Control event handlers.
    // Event handlers for view only mode.
    public doOnEditMilestones(event: any): void {
        if (this.site.siteIsBuildable) {
            AccessibilityHelper.preventDoubleHitOnEvent(event);
            
            this.onEdit.emit(this.siteMilestonesSummary.milestones);
        }
    }

    // Event handlers for edit mode.
    public openSiteMilestoneDialogue(milestoneParam: DataCollectionMilestone = null): void {
        // If no milestone is provided, create a new one.
        let milestone: DataCollectionMilestone = (milestoneParam ?? new DataCollectionMilestone());

        let dialogConfig: MatDialogConfig = new MatDialogConfig();
        dialogConfig.hasBackdrop = true;
        dialogConfig.width = '700px';
        dialogConfig.height = '450px';

        let availableMilestoneNames: string[] = this.getAvailableMilestoneNames(milestone);
        dialogConfig.data = new SiteMilestoneDialogData(milestone, availableMilestoneNames, this.siteMilestonesSummary.canScheduleEvents);

        const dialogRef = this.dialog.open(SiteMilestoneDialog, dialogConfig);

        dialogRef.afterClosed().subscribe(milestone => {
            if (milestone != null) {
                this.retrieveMilestones();
            }
        });
    }

    public deleteMilestone(eventData: any, milestoneParam: DataCollectionMilestone): void {
        let confirmationMessage: string = `Are you sure you want to delete milestone '${milestoneParam.name}'?`;
        let dialogueOptions: IConfirmationDialog = { hideNo: false, showOk: true, dialogType: ConfirmationDialogEnums.WARNING };
        let dialogueModel: ConfirmationDialogModel = new ConfirmationDialogModel(`Delete Milestone Confirmation`, confirmationMessage, dialogueOptions);
        let dialogRef = this.dialog.open(ConfirmationDialogComponent, { data: dialogueModel });
        dialogRef.afterClosed().subscribe(yes => {
            if (yes) {
                this.milestoneService.deleteSiteMilestone(milestoneParam.id).then(response => {
                    this.retrieveMilestones();
                });
            }
        });
    }

    // Private helper methods.
    private retrieveMilestones(): void {
        //this.milestones = [];

        // Note:  the following method will be enabled after creating a first milestone.
        /*
        this.milestoneService.getSiteMilestones(this.site.id).then(milestonesResponse => {
            this.milestones = milestonesResponse;
        });
        */
        this.milestoneService.getSiteMilestonesSummary(this.site.id).then(summaryResponse => {
            this.siteMilestonesSummary = summaryResponse;
        });
    }

    private getAvailableMilestoneNames(milestone: DataCollectionMilestone = null): string[] {
        let availableMilestoneNames: string[] = null;

        if ((milestone != null) && (milestone.id > 0))
            availableMilestoneNames = [milestone.name];
        else
            availableMilestoneNames = [SiteMilestoneName.SiteOpening, SiteMilestoneName.SiteClosing].filter(this.includeMilestoneName);

        return availableMilestoneNames;
    }

    private includeMilestoneName = (milestoneName: string): boolean => {
        let existingNamedMilestone: DataCollectionMilestone =
            //this.milestones.find(m => m.name == milestoneName);
            this.siteMilestonesSummary.milestones.find(m => m.name == milestoneName);

        return (existingNamedMilestone == null);
    }

}
